import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSiteSession } from '../../context/SiteSessionContext';
import { getMenuIcon, normalizeMenuConfig } from '../../lib/menuConfig';
import { getCollection, requestJson } from '../../lib/api';
import { getSocket } from '../../lib/socket';
import { useCoalescedRefresh } from '../../hooks/useCoalescedRefresh';

const secondaryListItems = [
];

function buildMenuTree(items) {
  const itemsById = new Map(items.map((item) => [item.id, { ...item, children: [] }]));
  const roots = [];

  itemsById.forEach((item) => {
    const parent = item.parentId ? itemsById.get(item.parentId) : null;
    if (parent) {
      parent.children.push(item);
    } else {
      roots.push(item);
    }
  });

  const sortItems = (entries) =>
    entries
      .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
      .map((item) => ({
        ...item,
        children: sortItems(item.children || []),
      }));

  return sortItems(roots);
}

function itemContainsPath(item, pathname) {
  return item.path === pathname || (item.children || []).some((child) => itemContainsPath(child, pathname));
}

function isVisible(item, { isAdmin, siteType }) {
  if (item.adminOnly && !isAdmin) return false;
  if (isAdmin) return true;
  if (siteType === 'workshop') {
    return [
      '/stock/verres',
      '/stock/reservations',
      '/stock/commandes',
      '/stock/reassort',
      '/suivi-atelier',
      '/suivi-atelier/bl',
      '/statistiques',
      '/statistiques/delais',
      '/statistiques/stock',
    ].includes(item.path);
  }
  if (item.permission) return false;
  if (siteType === 'sav') return ['/sav', '/sav-commandes', '/sav/documents', '/statistiques/logistique'].includes(item.path);
  return ['/suivi-atelier', '/suivi-atelier/bl', '/sav', '/sav/documents', '/clients', '/stock/verres', '/stock/reservations'].includes(item.path);
}

export default function MenuContent({ collapsed = false, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, site, siteType, hasPermission } = useSiteSession();
  const [menuConfig, setMenuConfig] = useState(() => normalizeMenuConfig());
  const [openGroups, setOpenGroups] = useState({ Production: true, Référentiel: true });
  const [pendingCounts, setPendingCounts] = useState({ logistics: 0, workshop: 0, stockFollowup: 0, reservations: 0, deliveryNotes: 0 });

  const loadMenuConfig = useCallback(async () => {
    try {
      const payload = await requestJson('/settings/MENU');
      setMenuConfig(normalizeMenuConfig(payload.value));
    } catch (_error) {
      setMenuConfig(normalizeMenuConfig());
    }
  }, []);

  useEffect(() => {
    loadMenuConfig();
    window.addEventListener('opticdev:menu-config-updated', loadMenuConfig);
    return () => window.removeEventListener('opticdev:menu-config-updated', loadMenuConfig);
  }, [loadMenuConfig]);

  const loadPendingCounts = useCallback(async () => {
    const canLoadStorePending = !isAdmin && siteType === 'store' && site?._id;
    const canLoadDispatchPending = !isAdmin && ['store', 'workshop'].includes(siteType) && site?._id;
    const canLoadReservations = isAdmin || siteType === 'store' || siteType === 'workshop';

    if (!canLoadStorePending && !canLoadReservations && !canLoadDispatchPending) {
      setPendingCounts({ logistics: 0, workshop: 0, stockFollowup: 0, reservations: 0, deliveryNotes: 0 });
      return;
    }

    try {
      const [savPayload, reservationsPayload, dispatchListsPayload, commandesPayload, stockCommandesPayload] = await Promise.all([
        canLoadStorePending ? requestJson('/sav-requests?limit=500') : Promise.resolve({ data: [] }),
        canLoadReservations ? requestJson('/stock-reservations') : Promise.resolve({ data: [] }),
        canLoadDispatchPending ? requestJson('/commandes/workflow/dispatch-lists') : Promise.resolve({ data: [] }),
        siteType === 'store' ? requestJson('/commandes?limit=500') : Promise.resolve({ data: [] }),
        siteType === 'workshop' ? requestJson('/commandes?view=stock&limit=500') : Promise.resolve({ data: [] }),
      ]);
      const dispatchLists = getCollection(dispatchListsPayload);
      const workshopDirection = siteType === 'workshop' ? 'store_to_workshop' : 'workshop_to_store';
      const workshopSiteField = siteType === 'workshop' ? 'workshopSiteId' : 'storeSiteId';
      const awaitingBlReception = dispatchLists.filter((list) => {
        const awaitingReceipt = list.status === 'sent' || list.status === 'pending';
        const listSiteId = list[workshopSiteField]?._id || list[workshopSiteField];
        return awaitingReceipt && list.direction === workshopDirection && String(listSiteId) === String(site?._id);
      }).length;
      const outgoingDirection = siteType === 'workshop' ? 'workshop_to_store' : 'store_to_workshop';
      const outgoingSiteField = siteType === 'workshop' ? 'workshopSiteId' : 'storeSiteId';
      const awaitingBlSend = dispatchLists.filter((list) => {
        const listSiteId = list[outgoingSiteField]?._id || list[outgoingSiteField];
        return list.status === 'draft' && list.direction === outgoingDirection && String(listSiteId) === String(site?._id);
      }).length;
      const storeDispatchStepCodes = new Set([
        'MAGASIN_ENVOI_DOSSIER_MONTURE',
        'MAGASIN_DOSSIER_INFORMATION',
        'MAGASIN_ENVOI_DOSSIER_MONTURE_VERRE',
        'MAGASIN_ENVOI_MONTURE',
      ]);
      const activeDispatchCommandeIds = new Set(
        dispatchLists
          .filter((list) => ['draft', 'sent', 'pending', 'partial'].includes(list.status))
          .flatMap((list) => list.items || [])
          .map((item) => String(item.commandeId?._id || item.commandeId || ''))
          .filter(Boolean)
      );
      const commandes = getCollection(commandesPayload);
      const jobsReadyForBl = siteType === 'store'
        ? commandes.filter((commande) => {
            const currentStep = (commande.workflow?.steps || []).find((step) => !step.completedAt);
            return (
              currentStep?.ownerType === 'store' &&
              storeDispatchStepCodes.has(currentStep.code) &&
              !activeDispatchCommandeIds.has(String(commande._id))
            );
          }).length
        : 0;
      const stockFollowup = siteType === 'workshop'
        ? getCollection(stockCommandesPayload).filter((commande) => {
            const currentStep = (commande.workflow?.steps || []).find((step) => !step.completedAt);
            const workshopId = commande.workshopSiteId?._id || commande.workshopSiteId;
            const isStock = commande.lens?.mode === 'stock' || ['STOCK_LENS_STORE', 'STOCK_LENS_WORKSHOP', 'STOCK_LENS_RAW_BL'].includes(commande.process);
            return isStock
              && currentStep?.code === 'ATELIER_RECUPERE_VERRE_STOCK'
              && String(workshopId) === String(site?._id);
          }).length
        : 0;
      const workshop = awaitingBlReception + jobsReadyForBl;
      const logistics = getCollection(savPayload)
        .filter((row) => row.status === 'waiting_store_info')
        .length;
      const reservations = getCollection(reservationsPayload).length;

      setPendingCounts({ logistics, workshop, stockFollowup, reservations, deliveryNotes: awaitingBlReception + awaitingBlSend });
    } catch (_error) {
      setPendingCounts({ logistics: 0, workshop: 0, stockFollowup: 0, reservations: 0, deliveryNotes: 0 });
    }
  }, [isAdmin, site, siteType]);

  const schedulePendingCountsRefresh = useCoalescedRefresh(loadPendingCounts, 350);

  useEffect(() => {
    loadPendingCounts();
    if (isAdmin || !['store', 'workshop'].includes(siteType)) return undefined;

    const socket = getSocket();
    socket.on('commandes:changed', schedulePendingCountsRefresh);
    socket.on('sav-requests:changed', schedulePendingCountsRefresh);
    socket.on('stock-items:changed', schedulePendingCountsRefresh);
    window.addEventListener('opticdev:logistics-updated', schedulePendingCountsRefresh);
    const interval = window.setInterval(schedulePendingCountsRefresh, 30000);

    return () => {
      socket.off('commandes:changed', schedulePendingCountsRefresh);
      socket.off('sav-requests:changed', schedulePendingCountsRefresh);
      socket.off('stock-items:changed', schedulePendingCountsRefresh);
      window.removeEventListener('opticdev:logistics-updated', schedulePendingCountsRefresh);
      window.clearInterval(interval);
    };
  }, [isAdmin, loadPendingCounts, schedulePendingCountsRefresh, siteType]);

  const visibleGroups = useMemo(
    () => {
      const flatItemsByGroup = menuConfig.items
        .filter((item) => item.enabled !== false)
        .filter((item) =>
          item.permission && hasPermission(item.permission)
            ? true
            : isVisible(item, { isAdmin, siteType })
        )
        .reduce((acc, item) => {
          acc[item.groupId] = [...(acc[item.groupId] || []), item];
          return acc;
        }, {});

      return menuConfig.groups
        .map((group) => ({
          ...group,
          items: buildMenuTree(flatItemsByGroup[group.id] || []),
        }))
        .filter((group) => group.items.length);
    },
    [hasPermission, isAdmin, menuConfig, siteType]
  );

  const renderItem = (item, { nested = false, depth = 0 } = {}) => {
    const hasChildren = (item.children || []).length > 0;
    const itemSelected = itemContainsPath(item, location.pathname);
    const itemOpen = collapsed || openGroups[item.id] || itemSelected;
    const pendingCount =
      item.path === '/sav'
        ? pendingCounts.logistics
        : item.path === '/suivi-atelier'
          ? pendingCounts.workshop
          : item.path === '/suivi-atelier/bl'
            ? pendingCounts.deliveryNotes
            : item.path === '/stock/verres'
              ? pendingCounts.stockFollowup
            : item.path === '/stock/reservations'
            ? pendingCounts.reservations
            : 0;
    const button = (
      <ListItemButton
        selected={itemSelected}
        disabled={item.disabled}
        onClick={() => {
          if (item.disabled) return;
          navigate(item.path);
          if (hasChildren) {
            setOpenGroups((current) => ({ ...current, [item.id]: true }));
          }
          onNavigate?.();
        }}
        sx={{
          borderRadius: 1.5,
          mb: 0.5,
          minHeight: 42,
          justifyContent: collapsed ? 'center' : 'flex-start',
          pl: collapsed ? 1 : nested ? 2.25 + depth * 1.5 : 1.5,
          pr: collapsed ? 1 : 1.5,
          '&.Mui-selected': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
            color: 'primary.main',
            fontWeight: 900,
            '& .MuiListItemIcon-root': { color: 'inherit' },
            '& .MuiListItemText-primary': { fontWeight: 900 },
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.18),
            },
          },
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 38, justifyContent: 'center' }}>
          <Badge
            color="error"
            badgeContent={pendingCount || undefined}
            invisible={!pendingCount || !collapsed}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                fontWeight: 950,
                minWidth: 17,
                height: 17,
                px: 0.45,
              },
            }}
          >
            {getMenuIcon(item.icon)}
          </Badge>
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: itemSelected ? 900 : nested ? 650 : 500 }}>
                  {item.label}
                </Typography>
                {pendingCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      mr: 1,
                      minWidth: 18,
                      height: 18,
                      px: 0.55,
                      borderRadius: 999,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'error.main',
                      color: 'error.contrastText',
                      fontSize: 10,
                      fontWeight: 950,
                      lineHeight: 1,
                    }}
                  >
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </Box>
                )}
                {hasChildren && (
                  <IconButton
                    size="small"
                    edge="end"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setOpenGroups((current) => ({ ...current, [item.id]: !itemOpen }));
                    }}
                    sx={{ mr: -1, color: 'inherit' }}
                  >
                    {itemOpen ? <ExpandLessRoundedIcon fontSize="small" /> : <ExpandMoreRoundedIcon fontSize="small" />}
                  </IconButton>
                )}
              </Stack>
            }
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
        {collapsed ? <Tooltip title={item.label} placement="right">{button}</Tooltip> : button}
        {hasChildren && (
          <Collapse in={itemOpen} timeout="auto" unmountOnExit={!collapsed}>
            <List dense disablePadding>
              {item.children.map((child) => renderItem(child, { nested: true, depth: depth + 1 }))}
            </List>
          </Collapse>
        )}
      </ListItem>
    );
  };

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>
        {visibleGroups.map((group) => {
          const isGroupSelected = group.items.some((item) => itemContainsPath(item, location.pathname));
          const groupOpen = true;

          return (
            <Stack key={group.label} sx={{ mb: 1.5 }}>
              {collapsed ? (
                <Tooltip title={group.label} placement="right">
                  <ListItemButton
                    onClick={() => {
                      navigate(group.items[0].path);
                      onNavigate?.();
                    }}
                    selected={isGroupSelected}
                    sx={{
                      borderRadius: 1.5,
                      minHeight: 42,
                      justifyContent: 'center',
                      px: 1,
                      '&.Mui-selected': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                        color: 'primary.main',
                      },
                    }}
                  >
                    {getMenuIcon(group.icon)}
                  </ListItemButton>
                </Tooltip>
              ) : (
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    minHeight: 24,
                    px: 1.5,
                    pt: 1,
                    pb: 1,
                    color: 'text.secondary',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      color: 'inherit',
                      fontWeight: 650,
                      letterSpacing: 0,
                      fontSize: 13,
                      lineHeight: 1.2,
                    }}
                  >
                    {group.label}
                  </Typography>
                </Stack>
              )}
              <Collapse in={groupOpen} timeout="auto" unmountOnExit={!collapsed}>
                <List dense disablePadding>
                  {group.items.map((item) => renderItem(item, { nested: true, depth: 0 }))}
                </List>
              </Collapse>
            </Stack>
          );
        })}
      </List>
      <List dense>{secondaryListItems.map((item) => renderItem(item))}</List>
    </Stack>
  );
}
